import { send } from '@actual-app/core/platform/client/connection';
import type { Handlers } from '@actual-app/core/types/handlers';

type HandlerInput<Key extends keyof Handlers> = Parameters<Handlers[Key]>[0];
type HandlerResult<Key extends keyof Handlers> = Awaited<
  ReturnType<Handlers[Key]>
>;
type HandlerPromise<Key extends keyof Handlers> = Promise<HandlerResult<Key>>;

export type FinancialDashboardLoadResult =
  HandlerResult<'finance/dashboard-load'>;

export type FinancialOnboardingState = HandlerResult<'finance/onboarding-load'>;

export type FinancialSettingsRequest =
  HandlerInput<'finance/settings-configure'>;

export type FixedIncomeRequest = HandlerInput<'finance/fixed-income-create'>;

export type FixedIncomeUpdateRequest =
  HandlerInput<'finance/fixed-income-update'>;

export type BudgetCycleRequest = HandlerInput<'finance/budget-cycle-create'>;

export type BudgetCycleUpdateRequest =
  HandlerInput<'finance/budget-cycle-update'>;

export type FinancialManagementState = HandlerResult<'finance/management-load'>;

export type FinancialManagementRequest =
  HandlerInput<'finance/management-load'>;

export type CommitmentTemplateRequest =
  HandlerInput<'finance/commitment-create'>;

export type FinancialGoalRequest = HandlerInput<'finance/goal-create'>;

export type ReadyFinancialDashboard = Extract<
  FinancialDashboardLoadResult,
  Readonly<{ status: 'ready' }>
>['dashboard'];

export type FinancialDashboardRequest = HandlerInput<'finance/dashboard-load'>;

export type FinancialMovementRequest = HandlerInput<'finance/expense-record'>;

export type FinancialConfirmationRequest =
  HandlerInput<'finance/expected-income-confirm'>;

export type CycleGoalContributionRequest =
  HandlerInput<'finance/cycle-goal-contribution-set'>;

export type GoalProgressRequest = HandlerInput<'finance/goal-progress-record'>;

export type ProductFinanceClient = Readonly<{
  loadIncomeDistributionRules?: () => HandlerPromise<'finance/income-distribution-rules-load'>;
  saveIncomeDistributionRules?: (
    input: HandlerInput<'finance/income-distribution-rules-save'>,
  ) => HandlerPromise<'finance/income-distribution-rules-save'>;
  loadOnboarding: () => Promise<FinancialOnboardingState>;
  configureFinancialSettings: (
    input: FinancialSettingsRequest,
  ) => HandlerPromise<'finance/settings-configure'>;
  createFixedIncomeTemplate: (
    input: FixedIncomeRequest,
  ) => HandlerPromise<'finance/fixed-income-create'>;
  updateFixedIncomeTemplate: (
    input: FixedIncomeUpdateRequest,
  ) => HandlerPromise<'finance/fixed-income-update'>;
  setFixedIncomeTemplateEnabled: (
    input: HandlerInput<'finance/fixed-income-enabled-set'>,
  ) => HandlerPromise<'finance/fixed-income-enabled-set'>;
  createBudgetCycle: (
    input: BudgetCycleRequest,
  ) => HandlerPromise<'finance/budget-cycle-create'>;
  updateBudgetCycle: (
    input: BudgetCycleUpdateRequest,
  ) => HandlerPromise<'finance/budget-cycle-update'>;
  setBudgetCycleEnabled: (
    input: HandlerInput<'finance/budget-cycle-enabled-set'>,
  ) => HandlerPromise<'finance/budget-cycle-enabled-set'>;
  loadFinancialManagement: (
    input: FinancialManagementRequest,
  ) => Promise<FinancialManagementState>;
  createCommitmentTemplate: (
    input: CommitmentTemplateRequest,
  ) => HandlerPromise<'finance/commitment-create'>;
  updateCommitmentTemplate: (
    input: HandlerInput<'finance/commitment-update'>,
  ) => HandlerPromise<'finance/commitment-update'>;
  setCommitmentTemplateEnabled: (
    input: HandlerInput<'finance/commitment-enabled-set'>,
  ) => HandlerPromise<'finance/commitment-enabled-set'>;
  createFinancialGoal: (
    input: FinancialGoalRequest,
  ) => HandlerPromise<'finance/goal-create'>;
  updateFinancialGoal: (
    input: HandlerInput<'finance/goal-update'>,
  ) => HandlerPromise<'finance/goal-update'>;
  deleteFinancialGoal: (
    input: HandlerInput<'finance/goal-delete'>,
  ) => HandlerPromise<'finance/goal-delete'>;
  loadDashboard: (
    input: FinancialDashboardRequest,
  ) => Promise<FinancialDashboardLoadResult>;
  configureLiquidAccounts: (
    input: HandlerInput<'finance/liquid-accounts-configure'>,
  ) => HandlerPromise<'finance/liquid-accounts-configure'>;
  recordExpense: (
    input: FinancialMovementRequest,
  ) => HandlerPromise<'finance/expense-record'>;
  recordExtraIncome: (
    input: HandlerInput<'finance/extra-income-record'>,
  ) => HandlerPromise<'finance/extra-income-record'>;
  useEmergencyFund?: (
    input: HandlerInput<'finance/emergency-fund-use'>,
  ) => HandlerPromise<'finance/emergency-fund-use'>;
  confirmExpectedIncome: (
    input: FinancialConfirmationRequest,
  ) => HandlerPromise<'finance/expected-income-confirm'>;
  confirmScheduledPayment: (
    input: HandlerInput<'finance/scheduled-payment-confirm'>,
  ) => HandlerPromise<'finance/scheduled-payment-confirm'>;
  updateMovement: (
    input: HandlerInput<'finance/movement-update'>,
  ) => HandlerPromise<'finance/movement-update'>;
  deleteMovement: (
    input: HandlerInput<'finance/movement-delete'>,
  ) => HandlerPromise<'finance/movement-delete'>;
  undoReconciliation: (
    input: HandlerInput<'finance/reconciliation-undo'>,
  ) => HandlerPromise<'finance/reconciliation-undo'>;
  setCycleGoalContribution: (
    input: CycleGoalContributionRequest,
  ) => HandlerPromise<'finance/cycle-goal-contribution-set'>;
  recordGoalProgress: (
    input: GoalProgressRequest,
  ) => HandlerPromise<'finance/goal-progress-record'>;
  deleteGoalProgressEntry: (
    input: HandlerInput<'finance/goal-progress-delete'>,
  ) => HandlerPromise<'finance/goal-progress-delete'>;
}>;

export const productFinanceClient: ProductFinanceClient = Object.freeze({
  loadIncomeDistributionRules: () =>
    send('finance/income-distribution-rules-load'),
  saveIncomeDistributionRules: input =>
    send('finance/income-distribution-rules-save', input),
  loadOnboarding: () => send('finance/onboarding-load'),
  configureFinancialSettings: input =>
    send('finance/settings-configure', input),
  createFixedIncomeTemplate: input =>
    send('finance/fixed-income-create', input),
  updateFixedIncomeTemplate: input =>
    send('finance/fixed-income-update', input),
  setFixedIncomeTemplateEnabled: input =>
    send('finance/fixed-income-enabled-set', input),
  createBudgetCycle: input => send('finance/budget-cycle-create', input),
  updateBudgetCycle: input => send('finance/budget-cycle-update', input),
  setBudgetCycleEnabled: input =>
    send('finance/budget-cycle-enabled-set', input),
  loadFinancialManagement: input => send('finance/management-load', input),
  createCommitmentTemplate: input => send('finance/commitment-create', input),
  updateCommitmentTemplate: input => send('finance/commitment-update', input),
  setCommitmentTemplateEnabled: input =>
    send('finance/commitment-enabled-set', input),
  createFinancialGoal: input => send('finance/goal-create', input),
  updateFinancialGoal: input => send('finance/goal-update', input),
  deleteFinancialGoal: input => send('finance/goal-delete', input),
  loadDashboard: input => send('finance/dashboard-load', input),
  configureLiquidAccounts: input =>
    send('finance/liquid-accounts-configure', input),
  recordExpense: input => send('finance/expense-record', input),
  recordExtraIncome: input => send('finance/extra-income-record', input),
  useEmergencyFund: input => send('finance/emergency-fund-use', input),
  confirmExpectedIncome: input =>
    send('finance/expected-income-confirm', input),
  confirmScheduledPayment: input =>
    send('finance/scheduled-payment-confirm', input),
  updateMovement: input => send('finance/movement-update', input),
  deleteMovement: input => send('finance/movement-delete', input),
  undoReconciliation: input => send('finance/reconciliation-undo', input),
  setCycleGoalContribution: input =>
    send('finance/cycle-goal-contribution-set', input),
  recordGoalProgress: input => send('finance/goal-progress-record', input),
  deleteGoalProgressEntry: input => send('finance/goal-progress-delete', input),
});
